import React, { useState, useEffect } from 'react'
import { Modal } from '../Modal'
import { Button } from '../Button'
import { Badge } from '../Badge'
import { updatePage, PageRecord } from '../../services/api'
import formStyles from '../../styles/forms.module.scss'

interface Props {
  page: PageRecord | null
  draftUpdatedAt?: string | null
  onClose: () => void
  onSaved: (updated: PageRecord) => void
}

const PageSettingsModal: React.FC<Props> = ({ page, draftUpdatedAt, onClose, onSaved }) => {
  const [name, setName] = useState('')
  const [path, setPath] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [isActive, setIsActive] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (page) {
      setName(page.name)
      setPath(page.path)
      setTitle(page.title || '')
      setDescription(page.description || '')
      setIsActive(page.is_active)
      setError(null)
    }
  }, [page])

  if (!page) return null

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Page name is required')
      return
    }
    if (!path.trim()) {
      setError('Path is required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const trimmedPath = path.trim()
      const normalizedPath = trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`
      const updated = await updatePage(page.id, {
        name: name.trim(),
        path: normalizedPath,
        title: title.trim() || null,
        description: description.trim() || null,
        is_active: isActive,
        draft_updated_at: draftUpdatedAt ?? '',
      })
      onSaved(updated)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Page Settings"
      size="small"
      footer={
        <>
          <Button variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button size="small" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </>
      }
    >
      <div className={formStyles.formField}>
        <div className={formStyles.fieldGroup}>
          <label>Name <Badge variant="neutral" size="small">Internal</Badge></label>
          <input value={name} onChange={e => setName(e.target.value)} />
        </div>

        <div className={formStyles.fieldGroup}>
          <label>Path <Badge variant="secondary" size="small">Public</Badge></label>
          <input value={path} onChange={e => setPath(e.target.value)} />
        </div>

        <div className={formStyles.fieldGroup}>
          <label>Title <Badge variant="secondary" size="small">Public</Badge></label>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Browser tab / OG title"
          />
        </div>

        <div className={formStyles.fieldGroup}>
          <label>Description <Badge variant="secondary" size="small">Public</Badge></label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Meta description for SEO / social sharing"
            rows={3}
          />
        </div>

        <label className={formStyles.checkboxLabel}>
          <input
            type="checkbox"
            checked={isActive}
            onChange={e => setIsActive(e.target.checked)}
          />
          Page is active
        </label>

        {error && <div className={formStyles.errorText}>{error}</div>}
      </div>
    </Modal>
  )
}

export default PageSettingsModal
